import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Repeat
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO, addMonths, subMonths, addDays, addWeeks } from 'date-fns';

const SchedulePage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(searchParams.get('action') === 'new');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const [newEvent, setNewEvent] = useState({
    title: '',
    event_type: 'practice',
    start_time: '',
    end_time: '',
    location: '',
    description: '',
    is_recurring: false,
    recurrence_pattern: 'weekly',
    recurrence_days: [],
    recurrence_end_date: ''
  });

  const eventTypes = [
    { value: 'practice', label: 'Practice', color: 'bg-aura-primary', icon: '🏃' },
    { value: 'game', label: 'Game', color: 'bg-aura-success', icon: '🏆' },
    { value: 'workout', label: 'Workout', color: 'bg-aura-secondary', icon: '💪' },
    { value: 'recovery', label: 'Recovery', color: 'bg-aura-warning', icon: '🧘' },
    { value: 'personal_training', label: 'Personal Training', color: 'bg-purple-500', icon: '🎯' },
    { value: 'conditioning', label: 'Conditioning', color: 'bg-orange-500', icon: '🔥' },
    { value: 'school', label: 'School Event', color: 'bg-blue-500', icon: '📚' },
    { value: 'family', label: 'Family Event', color: 'bg-pink-500', icon: '👨‍👩‍👧‍👦' },
    { value: 'appointment', label: 'Appointment', color: 'bg-cyan-500', icon: '📋' },
  ];

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/schedule`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('aura_token');
      
      if (newEvent.is_recurring && newEvent.recurrence_days.length > 0) {
        // Create recurring events
        const startDate = new Date(newEvent.start_time);
        const endRecurrence = newEvent.recurrence_end_date 
          ? new Date(newEvent.recurrence_end_date) 
          : addWeeks(startDate, 12); // Default to 12 weeks
        
        const events = [];
        let currentDate = startDate;
        
        // Map day names to day numbers (0 = Sunday, 1 = Monday, etc.)
        const dayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };
        const selectedDays = newEvent.recurrence_days.map(d => dayMap[d]);
        
        // Generate events for each occurrence
        while (currentDate <= endRecurrence) {
          const dayOfWeek = currentDate.getDay();
          
          if (selectedDays.includes(dayOfWeek)) {
            // Calculate start and end times for this occurrence
            const eventStart = new Date(currentDate);
            const originalStart = new Date(newEvent.start_time);
            const originalEnd = new Date(newEvent.end_time);
            
            eventStart.setHours(originalStart.getHours(), originalStart.getMinutes());
            
            const eventEnd = new Date(currentDate);
            eventEnd.setHours(originalEnd.getHours(), originalEnd.getMinutes());
            
            events.push({
              title: newEvent.title,
              event_type: newEvent.event_type,
              start_time: eventStart.toISOString(),
              end_time: eventEnd.toISOString(),
              location: newEvent.location,
              description: newEvent.description,
              is_recurring: true,
              recurrence_group: `rec_${Date.now()}`
            });
          }
          
          currentDate = addDays(currentDate, 1);
        }
        
        // Create all events
        for (const event of events) {
          await axios.post(`${API}/schedule`, event, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          });
        }
        
        toast.success(`Created ${events.length} recurring events!`);
      } else {
        // Create single event
        await axios.post(`${API}/schedule`, newEvent, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        toast.success('Event created!');
      }
      
      setShowNewDialog(false);
      setNewEvent({
        title: '',
        event_type: 'practice',
        start_time: '',
        end_time: '',
        location: '',
        description: '',
        is_recurring: false,
        recurrence_pattern: 'weekly',
        recurrence_days: [],
        recurrence_end_date: ''
      });
      fetchEvents();
    } catch (error) {
      toast.error('Failed to create event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.delete(`${API}/schedule/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const getEventTypeColor = (type) => {
    const eventType = eventTypes.find(t => t.value === type);
    return eventType?.color || 'bg-aura-text-subtle';
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = parseISO(event.start_time);
      return isSameDay(eventDate, date);
    });
  };

  const selectedDateEvents = getEventsForDate(selectedDate);

  // Get days in current month view
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  return (
    <DashboardLayout title="Schedule">
      <div className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Section Header */}
        <div className="mb-2">
          <p className="text-aura-text-muted text-sm">Follow the system. Improve every session.</p>
        </div>
        
        {/* Header with Add Button */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading font-bold text-xl text-white uppercase tracking-tight">
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
          </div>
          <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
            <DialogTrigger asChild>
              <Button className="bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider" data-testid="new-event-btn">
                <Plus className="w-5 h-5 mr-2" />
                Add Event
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-aura-surface border-white/10 max-w-md mx-4 max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                  New Event
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateEvent} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Event Title</Label>
                  <Input
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    placeholder="e.g., Team Practice"
                    required
                    className="h-12 bg-aura-bg border-white/10 text-white"
                    data-testid="event-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Event Type</Label>
                  <Select value={newEvent.event_type} onValueChange={(v) => setNewEvent({ ...newEvent, event_type: v })}>
                    <SelectTrigger className="h-12 bg-aura-bg border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      {eventTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value} className="text-white">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${type.color}`} />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Start Time</Label>
                    <Input
                      type="datetime-local"
                      value={newEvent.start_time}
                      onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                      required
                      className="h-12 bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">End Time</Label>
                    <Input
                      type="datetime-local"
                      value={newEvent.end_time}
                      onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                      required
                      className="h-12 bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Location</Label>
                  <Input
                    value={newEvent.location}
                    onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                    placeholder="e.g., Main Field"
                    className="h-12 bg-aura-bg border-white/10 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Notes</Label>
                  <Textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    placeholder="Any additional details..."
                    className="bg-aura-bg border-white/10 text-white min-h-[80px]"
                  />
                </div>

                {/* Recurring Event Section */}
                <div className="space-y-3 pt-2 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Repeat className="w-4 h-4 text-aura-secondary" />
                      <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Recurring Event</Label>
                    </div>
                    <button
                      type="button"
                      onClick={() => setNewEvent({ ...newEvent, is_recurring: !newEvent.is_recurring })}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        newEvent.is_recurring ? 'bg-aura-secondary' : 'bg-white/20'
                      }`}
                      data-testid="recurring-toggle"
                    >
                      <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        newEvent.is_recurring ? 'translate-x-7' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  {newEvent.is_recurring && (
                    <div className="space-y-3 p-3 bg-aura-secondary/10 rounded-lg border border-aura-secondary/30">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Repeat On</Label>
                        <div className="flex flex-wrap gap-2">
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                            <button
                              key={day}
                              type="button"
                              onClick={() => {
                                const days = newEvent.recurrence_days.includes(day)
                                  ? newEvent.recurrence_days.filter(d => d !== day)
                                  : [...newEvent.recurrence_days, day];
                                setNewEvent({ ...newEvent, recurrence_days: days });
                              }}
                              className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                                newEvent.recurrence_days.includes(day)
                                  ? 'bg-aura-secondary text-white'
                                  : 'bg-white/10 text-aura-text-muted hover:bg-white/20'
                              }`}
                              data-testid={`day-${day}`}
                            >
                              {day}
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-aura-text-subtle">
                          Select the days this event repeats
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Repeat Until</Label>
                        <Input
                          type="date"
                          value={newEvent.recurrence_end_date}
                          onChange={(e) => setNewEvent({ ...newEvent, recurrence_end_date: e.target.value })}
                          className="h-10 bg-aura-bg border-white/10 text-white"
                        />
                        <p className="text-xs text-aura-text-subtle">
                          Leave blank for 12 weeks (default)
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <Button type="submit" className="w-full bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider h-12" data-testid="save-event-btn">
                  {newEvent.is_recurring && newEvent.recurrence_days.length > 0 
                    ? 'Create Recurring Events' 
                    : 'Create Event'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Calendar Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="text-aura-text-muted hover:text-white"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <Button
            variant="ghost"
            onClick={() => setCurrentMonth(new Date())}
            className="text-aura-text-muted hover:text-white"
          >
            Today
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="text-aura-text-muted hover:text-white"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Calendar Grid */}
        <Card className="bg-aura-surface border-white/10">
          <CardContent className="p-4">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="text-center text-xs text-aura-text-muted uppercase tracking-wider py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Days Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Empty cells for days before month starts */}
              {Array.from({ length: monthStart.getDay() }).map((_, i) => (
                <div key={`empty-start-${i}`} className="aspect-square" />
              ))}
              
              {daysInMonth.map((day) => {
                const dayEvents = getEventsForDate(day);
                const isToday = isSameDay(day, new Date());
                const isSelected = isSameDay(day, selectedDate);

                return (
                  <button
                    key={day.toISOString()}
                    onClick={() => setSelectedDate(day)}
                    className={`aspect-square p-1 rounded-lg transition-colors relative ${
                      isSelected
                        ? 'bg-aura-primary'
                        : isToday
                        ? 'bg-white/10'
                        : 'hover:bg-white/5'
                    }`}
                  >
                    <span className={`text-sm font-medium ${
                      isSelected ? 'text-white' : isToday ? 'text-aura-primary' : 'text-white'
                    }`}>
                      {format(day, 'd')}
                    </span>
                    {dayEvents.length > 0 && (
                      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                        {dayEvents.slice(0, 3).map((event, i) => (
                          <div
                            key={i}
                            className={`w-1.5 h-1.5 rounded-full ${getEventTypeColor(event.event_type)}`}
                          />
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Selected Date Events */}
        <div>
          <h3 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-4">
            {format(selectedDate, 'EEEE, MMMM d')}
          </h3>
          
          {selectedDateEvents.length > 0 ? (
            <div className="space-y-3">
              {selectedDateEvents.map((event) => (
                <Card key={event.event_id} className="bg-aura-surface border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`w-1 h-full min-h-[60px] rounded-full ${getEventTypeColor(event.event_type)}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium text-white">{event.title}</h4>
                            <Badge variant="outline" className="text-xs capitalize">
                              {event.event_type}
                            </Badge>
                            {event.is_recurring && (
                              <Badge variant="outline" className="text-xs text-aura-secondary border-aura-secondary/50">
                                <Repeat className="w-3 h-3 mr-1" />
                                Recurring
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm text-aura-text-muted">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {format(parseISO(event.start_time), 'h:mm a')} - {format(parseISO(event.end_time), 'h:mm a')}
                            </span>
                            {event.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {event.location}
                              </span>
                            )}
                          </div>
                          {event.description && (
                            <p className="text-sm text-aura-text-subtle mt-2">{event.description}</p>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteEvent(event.event_id)}
                        className="text-aura-text-muted hover:text-aura-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-8 text-center">
                <CalendarIcon className="w-12 h-12 text-aura-text-subtle mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg text-white mb-2">No Events</h3>
                <p className="text-aura-text-muted mb-4">
                  Nothing scheduled for this day
                </p>
                <Button 
                  onClick={() => setShowNewDialog(true)}
                  className="bg-aura-primary hover:bg-aura-primary-hover text-white"
                >
                  Add Event
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Upcoming Events List */}
        <div>
          <h3 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-4">
            Upcoming Events
          </h3>
          
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aura-card p-4 animate-pulse">
                  <div className="h-16 bg-white/5 rounded-lg" />
                </div>
              ))}
            </div>
          ) : events.filter(e => new Date(e.start_time) >= new Date()).length > 0 ? (
            <div className="space-y-3">
              {events
                .filter(e => new Date(e.start_time) >= new Date())
                .slice(0, 5)
                .map((event) => (
                  <Card key={event.event_id} className="bg-aura-surface border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-lg ${getEventTypeColor(event.event_type)} bg-opacity-20 flex items-center justify-center`}>
                          <CalendarIcon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-white truncate">{event.title}</h4>
                          <p className="text-sm text-aura-text-muted">
                            {format(parseISO(event.start_time), 'EEE, MMM d • h:mm a')}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          ) : (
            <p className="text-aura-text-muted text-center py-4">No upcoming events</p>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SchedulePage;
